"use client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "../reveal";
import { ExtractBySectionType, ExtractPropertyType, SectionType } from "types";
import { PortableText } from "next-sanity";
import Link from "next/link";

type FAQSectionType = ExtractBySectionType<SectionType, "faqSection", "_type">;

export type FAQsType = ExtractPropertyType<FAQSectionType, "faqs">;

const portableTextComponents = {
  marks: {
    internalLink: ({ children, value }: { children: React.ReactNode; value?: { reference?: { _type?: string; slug?: string } } }) => {
      const ref = value?.reference;
      if (!ref?.slug) return <>{children}</>;
      const href = ref._type === "guide" ? `/guides/${ref.slug}` : `/${ref.slug}`;
      return (
        <Link href={href} className="text-primary-400 underline underline-offset-4 hover:text-primary-600">
          {children}
        </Link>
      );
    },
  },
};

export function FAQsSection({ data }: { data: FAQSectionType }) {
  const { heading, subheading, faqs } = data;
  return (
    <section className="max-w-screen-2xl mx-auto px-6 sm:px-24 py-20 bg-white">
      <Reveal>
        <h3 className="font-medium text-2xl lg:text-5xl mb-5 text-center">
          {heading}
        </h3>
        <p className="font-normal text-center mb-8">{subheading}</p>
      </Reveal>
      {faqs && <FAQs faqs={faqs.slice(0, 5)} />}
    </section>
  );
}

export function FAQs({ faqs }: { faqs: FAQsType }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      {faqs.map((faq, idx) => (
        <AccordionItem
          value={`item-${idx}`}
          key={idx}
          className="p-4 data-[state=open]:bg-[rgba(247,_249,_252,_1)] lg:px-14 lg:py-8"
        >
          <Reveal>
            <AccordionTrigger className="text-lg">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="dark text-primary-text-900 text-base lg:text-lg">
              <PortableText value={faq.answer} components={portableTextComponents} />
            </AccordionContent>
          </Reveal>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
